import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import PinDisplay from "@/components/PinDisplay";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";

const STATUS_LABEL = {
  pending_payment: { label: "Awaiting Payment", color: "bg-amber-100 text-amber-700" },
  paid: { label: "PIN Active", color: "bg-emerald-100 text-emerald-700" },
  active: { label: "In Progress", color: "bg-emerald-100 text-emerald-700" },
  completed: { label: "Completed", color: "bg-slate-100 text-slate-600" },
  cancelled: { label: "Cancelled", color: "bg-red-100 text-red-700" },
};

export default function Dashboard() {
  const { user, loading } = useAuth();
  const cart = useCart();
  const [bookings, setBookings] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [addOnFor, setAddOnFor] = useState(null);
  const [reorderingId, setReorderingId] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && !user) navigate("/login?next=/dashboard");
  }, [loading, user, navigate]);

  const refresh = async () => {
    setLoadingList(true);
    try {
      const { data } = await api.get("/bookings/me");
      setBookings(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load bookings");
    } finally {
      setLoadingList(false);
    }
  };

  useEffect(() => {
    if (user) refresh();
  }, [user]);

  if (loading || !user) return null;

  const reorder = async (bookingId) => {
    setReorderingId(bookingId);
    try {
      const { data } = await api.post(`/bookings/reorder/${bookingId}`);
      cart.reorderFrom(data);
      toast.success("Pack restored from your previous order");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Reorder failed");
    } finally {
      setReorderingId(null);
    }
  };

  const active = bookings.find((b) => b.status === "paid" || b.status === "active");

  return (
    <div className="bg-brand-slate min-h-screen">
      <Navbar />

      <main className="max-w-6xl mx-auto px-4 py-10">
        {/* Greeting */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-10">
          <div>
            <p className="text-[10px] font-black uppercase tracking-[0.4em] text-brand-red mb-2">
              Customer Dashboard
            </p>
            <h1 className="text-4xl md:text-5xl font-black uppercase tracking-tightest text-brand-blue">
              Hey, {user.name.split(" ")[0]}.
            </h1>
            <p className="text-slate-500 font-bold text-sm mt-2">
              Manage your active rental and pod access.
            </p>
          </div>
          <Link
            to="/#inventory"
            className="btn-primary px-6 py-3 rounded-xl font-black uppercase text-xs tracking-widest inline-flex items-center gap-2 self-start"
            data-testid="dashboard-book-more-link"
          >
            <i className="fas fa-plus" /> Book More Gear
          </Link>
        </div>

        {/* ID verification reminder */}
        {!user.id_verified && (
          <Link
            to="/profile"
            className="block mb-8 bg-brand-blue text-white rounded-[28px] px-6 py-5 hover:bg-brand-blue/90 transition-colors group"
            data-testid="dashboard-verify-banner"
          >
            <div className="flex flex-col md:flex-row md:items-center gap-4">
              <div className="w-12 h-12 rounded-2xl bg-brand-gold text-brand-blue flex items-center justify-center flex-shrink-0">
                <i className="fas fa-id-card text-xl" />
              </div>
              <div className="flex-1">
                <p className="text-[10px] font-black uppercase tracking-[0.3em] text-brand-gold">
                  Action Required
                </p>
                <p className="text-base md:text-lg font-black uppercase tracking-tightest mt-0.5">
                  One-time photo ID verification
                </p>
                <p className="text-[11px] text-white/80 font-medium mt-0.5 leading-snug">
                  60 seconds. Upload license front + back + a selfie. Never asked again.
                </p>
              </div>
              <span className="text-[10px] font-black uppercase tracking-widest bg-brand-gold text-brand-blue px-4 py-2 rounded-xl group-hover:bg-amber-300 transition-colors">
                Verify <i className="fas fa-arrow-right ml-2" />
              </span>
            </div>
          </Link>
        )}

        {/* Active rental — PIN */}
        {active ? (
          <div className="space-y-6 mb-12">
            <PinDisplay
              pin={active.pin}
              podAddress={active.pod_address}
              startsAt={active.pin_starts_at}
              endsAt={active.pin_ends_at}
            />

            <div className="grid md:grid-cols-3 gap-4">
              <Stat label="Order Ref" value={`#${active.id.slice(0, 8).toUpperCase()}`} icon="fa-receipt" />
              <Stat label="Items Out" value={active.items.reduce((s, i) => s + i.quantity, 0)} icon="fa-box-open" />
              <Stat label="Total Paid" value={`$${active.total.toFixed(2)}`} icon="fa-dollar-sign" />
            </div>

            <div className="bg-white rounded-[32px] border border-slate-100 shadow-sm overflow-hidden">
              <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
                <h3 className="font-black uppercase text-brand-blue tracking-tightest">Gear in this pack</h3>
                <button
                  onClick={() => setAddOnFor(active)}
                  className="bg-brand-gold text-brand-blue px-4 py-2 rounded-lg font-black uppercase text-[10px] tracking-widest hover:bg-amber-300 transition-colors"
                  data-testid="dashboard-add-on-button"
                >
                  <i className="fas fa-plus mr-2" /> Add to Pack
                </button>
              </div>
              <ul className="divide-y divide-slate-50">
                {active.items.map((it) => (
                  <li
                    key={it.item_id}
                    className="px-6 py-4 flex items-center gap-4"
                    data-testid={`active-item-${it.item_id}`}
                  >
                    <div className="w-10 h-10 rounded-xl bg-brand-blue text-brand-gold flex items-center justify-center">
                      <i className={`fas ${it.icon}`} />
                    </div>
                    <div className="flex-1">
                      <p className="font-black text-xs uppercase text-brand-blue">{it.name}</p>
                      <p className="text-[10px] font-bold text-slate-500">
                        ${it.price_per_day.toFixed(2)}/day × {it.quantity} × {active.rental_days}d
                      </p>
                    </div>
                    <p className="font-black text-sm text-brand-red">${it.line_total.toFixed(2)}</p>
                  </li>
                ))}
              </ul>
              {active.addons?.length > 0 && (
                <div className="bg-amber-50 px-6 py-3 border-t border-amber-100">
                  <p className="text-[10px] font-black uppercase tracking-widest text-amber-800">
                    <i className="fas fa-bolt mr-1.5" /> {active.addons.length} mid-event add-on
                    {active.addons.length > 1 ? "s" : ""} billed to card on file
                  </p>
                </div>
              )}
            </div>
          </div>
        ) : (
          <EmptyState />
        )}

        {/* History */}
        <div className="mt-12">
          <h3 className="text-2xl font-black uppercase tracking-tightest text-brand-blue mb-4">
            All Orders
          </h3>
          {loadingList ? (
            <p className="text-slate-500 font-bold">Loading...</p>
          ) : bookings.length === 0 ? (
            <p className="text-slate-500 font-bold">No orders yet.</p>
          ) : (
            <div className="bg-white rounded-[32px] border border-slate-100 shadow-sm overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-[10px] uppercase font-black tracking-widest text-slate-500">
                  <tr>
                    <th className="px-6 py-3 text-left">Ref</th>
                    <th className="px-6 py-3 text-left">Date</th>
                    <th className="px-6 py-3 text-left">Items</th>
                    <th className="px-6 py-3 text-right">Total</th>
                    <th className="px-6 py-3 text-right">Status</th>
                    <th className="px-6 py-3 text-right">Reorder</th>
                  </tr>
                </thead>
                <tbody>
                  {bookings.map((b) => {
                    const s = STATUS_LABEL[b.status] || STATUS_LABEL.pending_payment;
                    return (
                      <tr key={b.id} className="border-t border-slate-50" data-testid={`booking-row-${b.id}`}>
                        <td className="px-6 py-4 font-black text-brand-blue">
                          #{b.id.slice(0, 8).toUpperCase()}
                        </td>
                        <td className="px-6 py-4 font-bold text-slate-600">
                          {new Date(b.start_date).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4 font-bold text-slate-600">
                          {b.items.reduce((s, i) => s + i.quantity, 0)} pcs
                        </td>
                        <td className="px-6 py-4 text-right font-black text-brand-red">
                          ${b.total.toFixed(2)}
                        </td>
                        <td className="px-6 py-4 text-right">
                          <span className={`px-3 py-1 rounded-full text-[9px] font-black uppercase tracking-widest ${s.color}`}>
                            {s.label}
                          </span>
                          {b.status === "pending_payment" && (
                            <Link
                              to={`/booking/${b.id}`}
                              className="ml-2 text-[10px] font-black uppercase tracking-widest text-brand-blue underline"
                              data-testid={`booking-pay-link-${b.id}`}
                            >
                              Pay
                            </Link>
                          )}
                        </td>
                        <td className="px-6 py-4 text-right">
                          {["paid", "active", "completed"].includes(b.status) ? (
                            <button
                              onClick={() => reorder(b.id)}
                              disabled={reorderingId === b.id}
                              className="text-[10px] font-black uppercase tracking-widest text-brand-blue hover:text-brand-red transition-colors inline-flex items-center gap-1.5 disabled:opacity-50"
                              data-testid={`booking-reorder-${b.id}`}
                            >
                              {reorderingId === b.id ? (
                                <><i className="fas fa-spinner fa-spin" /> Loading</>
                              ) : (
                                <><i className="fas fa-rotate-right" /> Reorder</>
                              )}
                            </button>
                          ) : (
                            <span className="text-slate-300">—</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>

      <Footer />

      <AddOnDialog
        booking={addOnFor}
        onClose={() => setAddOnFor(null)}
        onSuccess={refresh}
      />
    </div>
  );
}

function Stat({ label, value, icon }) {
  return (
    <div className="bg-white rounded-[24px] p-5 border border-slate-100 shadow-sm flex items-center gap-3" data-testid={`dashboard-stat-${label.toLowerCase().replace(/ /g, "-")}`}>
      <div className="w-12 h-12 rounded-2xl bg-brand-blue text-brand-gold flex items-center justify-center">
        <i className={`fas ${icon}`} />
      </div>
      <div>
        <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">{label}</p>
        <p className="text-2xl font-black text-brand-blue">{value}</p>
      </div>
    </div>
  );
}

function EmptyState() {
  return (
    <div className="bg-white rounded-[40px] p-10 md:p-16 text-center border border-dashed border-slate-200" data-testid="dashboard-empty-state">
      <div className="w-20 h-20 mx-auto rounded-3xl bg-brand-blue text-brand-gold flex items-center justify-center mb-6">
        <i className="fas fa-lock-open text-3xl" />
      </div>
      <h2 className="text-3xl font-black uppercase tracking-tightest text-brand-blue mb-2">
        No active pin
      </h2>
      <p className="text-slate-500 font-bold mb-6 max-w-md mx-auto">
        Book gear from the Park Hill Pod and your 6-digit unlock code will appear here instantly.
      </p>
      <Link
        to="/#inventory"
        className="btn-primary inline-flex items-center gap-3 px-8 py-4 rounded-2xl font-black uppercase tracking-widest text-sm"
      >
        <i className="fas fa-bolt" /> Browse the Pod
      </Link>
    </div>
  );
}

function AddOnDialog({ booking, onClose, onSuccess }) {
  const [items, setItems] = useState([]);
  const [pick, setPick] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!booking) return;
    api.get("/inventory").then(({ data }) => setItems(data));
    setPick({});
  }, [booking]);

  if (!booking) return null;

  const total = items.reduce((s, it) => s + (pick[it.id] || 0) * it.price_per_day * booking.rental_days, 0);

  const goPay = async () => {
    const cartItems = Object.entries(pick)
      .filter(([, q]) => q > 0)
      .map(([item_id, q]) => ({ item_id, quantity: q }));
    if (cartItems.length === 0) {
      toast.error("Select gear to add");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/checkout/addon", {
        booking_id: booking.id,
        items: cartItems,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Add-on failed");
      setBusy(false);
    }
  };

  return (
    <Dialog open={!!booking} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl bg-white rounded-[32px] p-0 overflow-hidden" data-testid="addon-dialog">
        <DialogHeader className="bg-brand-blue text-white px-6 py-5 m-0">
          <DialogTitle className="text-white font-black uppercase tracking-tightest text-2xl">
            Add to Active Pack
          </DialogTitle>
          <p className="text-[10px] font-black uppercase tracking-widest text-brand-gold">
            Billed to card on file • Same {booking.rental_days}-day window
          </p>
        </DialogHeader>
        <div className="px-6 py-5 max-h-[60vh] overflow-y-auto">
          <div className="grid sm:grid-cols-2 gap-3">
            {items.map((it) => (
              <div
                key={it.id}
                className="flex items-center gap-3 p-3 rounded-2xl border border-slate-200"
                data-testid={`addon-item-${it.id}`}
              >
                <div className="w-10 h-10 rounded-xl bg-brand-blue text-brand-gold flex items-center justify-center">
                  <i className={`fas ${it.icon}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-black text-xs uppercase text-brand-blue truncate">{it.name}</p>
                  <p className="text-[10px] font-bold text-brand-red">
                    ${it.price_per_day.toFixed(2)}/day
                  </p>
                </div>
                <div className="flex items-center bg-slate-100 rounded-lg p-1">
                  <button
                    onClick={() => setPick((p) => ({ ...p, [it.id]: Math.max(0, (p[it.id] || 0) - 1) }))}
                    className="w-6 h-6 rounded text-brand-blue"
                    data-testid={`addon-${it.id}-decrement`}
                  >
                    <i className="fas fa-minus text-[9px]" />
                  </button>
                  <span className="w-7 text-center font-black text-xs" data-testid={`addon-${it.id}-qty`}>
                    {pick[it.id] || 0}
                  </span>
                  <button
                    onClick={() => setPick((p) => ({ ...p, [it.id]: (p[it.id] || 0) + 1 }))}
                    className="w-6 h-6 rounded bg-brand-blue text-white"
                    data-testid={`addon-${it.id}-increment`}
                  >
                    <i className="fas fa-plus text-[9px]" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="border-t border-slate-100 bg-slate-50 px-6 py-4 flex items-center justify-between">
          <div>
            <p className="text-[10px] font-black uppercase tracking-widest text-slate-500">Add-on total</p>
            <p className="text-2xl font-black text-brand-blue" data-testid="addon-total">
              ${total.toFixed(2)}
            </p>
          </div>
          <button
            onClick={goPay}
            disabled={busy || total === 0}
            className="btn-primary px-6 py-3 rounded-xl font-black uppercase text-xs tracking-widest disabled:opacity-50"
            data-testid="addon-checkout-button"
          >
            {busy ? <><i className="fas fa-spinner fa-spin mr-2" /> Charging</> : <>Charge & Add</>}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
